import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  Customer,
  CustomerAddress,
  ContactPerson,
  CustomerPhone,
} from '../models/customer.model';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe-decorator';
import { AddACH } from '../models/invoice/add-ach.model';
import { ACH } from '../models/invoice/ach.model';

@Injectable({
  providedIn: 'root',
})
export class CustomerManagementService {
  private baseRoute: string;
  private baseTarget: string;
  private headers: HttpHeaders;
  private mockCustomerAddresses: BehaviorSubject<CustomerAddress[]>;
  states: Array<object> = [
    {
      name: 'Alabama',
      abbreviation: 'AL',
    },
    {
      name: 'Alaska',
      abbreviation: 'AK',
    },
    {
      name: 'American Samoa',
      abbreviation: 'AS',
    },
    {
      name: 'Arizona',
      abbreviation: 'AZ',
    },
    {
      name: 'Arkansas',
      abbreviation: 'AR',
    },
    {
      name: 'California',
      abbreviation: 'CA',
    },
    {
      name: 'Colorado',
      abbreviation: 'CO',
    },
    {
      name: 'Connecticut',
      abbreviation: 'CT',
    },
    {
      name: 'Delaware',
      abbreviation: 'DE',
    },
    {
      name: 'District Of Columbia',
      abbreviation: 'DC',
    },
    {
      name: 'Federated States Of Micronesia',
      abbreviation: 'FM',
    },
    {
      name: 'Florida',
      abbreviation: 'FL',
    },
    {
      name: 'Georgia',
      abbreviation: 'GA',
    },
    {
      name: 'Guam',
      abbreviation: 'GU',
    },
    {
      name: 'Hawaii',
      abbreviation: 'HI',
    },
    {
      name: 'Idaho',
      abbreviation: 'ID',
    },
    {
      name: 'Illinois',
      abbreviation: 'IL',
    },
    {
      name: 'Indiana',
      abbreviation: 'IN',
    },
    {
      name: 'Iowa',
      abbreviation: 'IA',
    },
    {
      name: 'Kansas',
      abbreviation: 'KS',
    },
    {
      name: 'Kentucky',
      abbreviation: 'KY',
    },
    {
      name: 'Louisiana',
      abbreviation: 'LA',
    },
    {
      name: 'Maine',
      abbreviation: 'ME',
    },
    {
      name: 'Marshall Islands',
      abbreviation: 'MH',
    },
    {
      name: 'Maryland',
      abbreviation: 'MD',
    },
    {
      name: 'Massachusetts',
      abbreviation: 'MA',
    },
    {
      name: 'Michigan',
      abbreviation: 'MI',
    },
    {
      name: 'Minnesota',
      abbreviation: 'MN',
    },
    {
      name: 'Mississippi',
      abbreviation: 'MS',
    },
    {
      name: 'Missouri',
      abbreviation: 'MO',
    },
    {
      name: 'Montana',
      abbreviation: 'MT',
    },
    {
      name: 'Nebraska',
      abbreviation: 'NE',
    },
    {
      name: 'Nevada',
      abbreviation: 'NV',
    },
    {
      name: 'New Hampshire',
      abbreviation: 'NH',
    },
    {
      name: 'New Jersey',
      abbreviation: 'NJ',
    },
    {
      name: 'New Mexico',
      abbreviation: 'NM',
    },
    {
      name: 'New York',
      abbreviation: 'NY',
    },
    {
      name: 'North Carolina',
      abbreviation: 'NC',
    },
    {
      name: 'North Dakota',
      abbreviation: 'ND',
    },
    {
      name: 'Northern Mariana Islands',
      abbreviation: 'MP',
    },
    {
      name: 'Ohio',
      abbreviation: 'OH',
    },
    {
      name: 'Oklahoma',
      abbreviation: 'OK',
    },
    {
      name: 'Oregon',
      abbreviation: 'OR',
    },
    {
      name: 'Palau',
      abbreviation: 'PW',
    },
    {
      name: 'Pennsylvania',
      abbreviation: 'PA',
    },
    {
      name: 'Puerto Rico',
      abbreviation: 'PR',
    },
    {
      name: 'Rhode Island',
      abbreviation: 'RI',
    },
    {
      name: 'South Carolina',
      abbreviation: 'SC',
    },
    {
      name: 'South Dakota',
      abbreviation: 'SD',
    },
    {
      name: 'Tennessee',
      abbreviation: 'TN',
    },
    {
      name: 'Texas',
      abbreviation: 'TX',
    },
    {
      name: 'Utah',
      abbreviation: 'UT',
    },
    {
      name: 'Vermont',
      abbreviation: 'VT',
    },
    {
      name: 'Virgin Islands',
      abbreviation: 'VI',
    },
    {
      name: 'Virginia',
      abbreviation: 'VA',
    },
    {
      name: 'Washington',
      abbreviation: 'WA',
    },
    {
      name: 'West Virginia',
      abbreviation: 'WV',
    },
    {
      name: 'Wisconsin',
      abbreviation: 'WI',
    },
    {
      name: 'Wyoming',
      abbreviation: 'WY',
    },
  ];

  constructor(private httpClient: HttpClient) {
    this.baseRoute = environment.customerAPIRoot;
    this.baseTarget = 'customer';
    this.headers = new HttpHeaders();
    this.mockCustomerAddresses = new BehaviorSubject<CustomerAddress[]>([]);
  }

  addMockCustomerAddress(customerAddress: CustomerAddress) {
    let items = this.mockCustomerAddresses.getValue();
    items.push(customerAddress);
    this.mockCustomerAddresses.next(items);
  }

  getCustomer(id: number): Observable<Customer> {
    return this.httpClient.get<Customer>(
      `${this.baseRoute}/${this.baseTarget}/get/${id}`
    );
  }

  getAllCustomers<T>(): Observable<T> {
    return this.httpClient.get<T>(
      `${this.baseRoute}/${this.baseTarget}/getall`
    );
  }

  @AutoUnsubscribe()
  getAllCustomersWAddressPhone<T>(isShortAddress: boolean): Observable<T> {
    return this.httpClient.get<T>(
      `${this.baseRoute}/${this.baseTarget}/addressphone/getall?isShortAddress=${isShortAddress}`
    );
  }

  getCustomerContactPersons<T>(
    customerid: number
  ): Observable<ContactPerson[]> {
    return this.httpClient.get<ContactPerson[]>(
      `${this.baseRoute}/${this.baseTarget}/customercontactperson/get/${customerid}`
    );
  }

  getCustomerContactPersonById<T>(id: number): Observable<ContactPerson> {
    return this.httpClient.get<ContactPerson>(
      `${this.baseRoute}/customercontactperson/get/${id}`
    );
  }

  getCustomerPhones<T>(customerid: number): Observable<CustomerPhone[]> {
    return this.httpClient.get<CustomerPhone[]>(
      `${this.baseRoute}/${this.baseTarget}/phone/get/${customerid}`
    );
  }

  getContactPhones<T>(contactid: number): Observable<CustomerPhone[]> {
    return this.httpClient.get<CustomerPhone[]>(
      `${this.baseRoute}/customercontactperson/phone/get/${contactid}`
    );
  }

  getCustomerAddresses<T>(customerid: number): Observable<CustomerAddress[]> {
    return this.httpClient.get<CustomerAddress[]>(
      `${this.baseRoute}/${this.baseTarget}/address/get/${customerid}`
    );
  }

  getContactAddresses<T>(contactid: number): Observable<CustomerAddress[]> {
    return this.httpClient.get<CustomerAddress[]>(
      `${this.baseRoute}/customercontactperson/address/get/${contactid}`
    );
  }

  getCustomerCardNumber<T>(id: number): Observable<T> {
    return this.httpClient.get<T>(
      `${this.baseRoute}/customercardnumber/get/${id}`
    );
  }

  getCustomerCardNumbers<T>(customerid: number): Observable<T> {
    return this.httpClient.get<T>(
      `${this.baseRoute}/${this.baseTarget}/customercardnumber/get/${customerid}`
    );
  }

  getCustomerSubscription<T>(id: number): Observable<T> {
    return this.httpClient.get<T>(
      `${this.baseRoute}/customersubscription/get/${id}`
    );
  }

  getCustomerSubscriptions<T>(customerid: number): Observable<T> {
    return this.httpClient.get<T>(
      `${this.baseRoute}/${this.baseTarget}/customersubscription/get/${customerid}`
    );
  }

  addCustomer<T>(payload: any): Observable<T> {
    return this.httpClient.post<T>(
      `${this.baseRoute}/${this.baseTarget}/add`,
      payload
    );
  }

  addContactPerson<T>(payload: any): Observable<T> {
    return this.httpClient.post<T>(
      `${this.baseRoute}/customercontactperson/add`,
      payload
    );
  }

  updateContactPerson<T>(payload: any): Observable<T> {
    return this.httpClient.post<T>(
      `${this.baseRoute}/customercontactperson/update`,
      payload
    );
  }

  addAddress<T>(payload: any): Observable<T> {
    return this.httpClient.post<T>(
      `${this.baseRoute}/ecommerce/customer/address/addupdate`,
      payload
    );
  }

  addPhone<T>(payload: any): Observable<T> {
    return this.httpClient.post<T>(
      `${this.baseRoute}/phone/addupdate`,
      payload
    );
  }

  addCustomerCardNumber<T>(payload: any): Observable<T> {
    return this.httpClient.post<T>(
      `${this.baseRoute}/customercardnumber/add`,
      payload
    );
  }

  updateCustomer<T>(payload: any): Observable<T> {
    return this.httpClient.post<T>(
      `${this.baseRoute}/${this.baseTarget}/update`,
      payload
    );
  }

  updateCustomerCardNumber<T>(payload: any): Observable<T> {
    return this.httpClient.post<T>(
      `${this.baseRoute}/customercardnumber/update`,
      payload
    );
  }

  updateCardToken(payload: any): Observable<string> {
    return this.httpClient.post(
      `${environment.paymentAPIRoot}/api/credit/updatetoken`,
      payload, { responseType: 'text' }
    );
  }

  deactivateCustomer<T>(id: number, updatedBy: number): Observable<T> {
    //todo transfer updatedBy to backend
    return this.httpClient.post<T>(
      `${this.baseRoute}/${this.baseTarget}/deactivate/${id}/${updatedBy}`,
      id
    );
  }

  deleteCustomer<T>(id: number, updatedBy: number): Observable<T> {
    //todo transfer updatedBy to backend
    return this.httpClient.delete<T>(
      `${this.baseRoute}/${this.baseTarget}/delete/${id}/${updatedBy}`
    );
  }

  deleteCustomerCardNumber<T>(id: number, updatedBy: number): Observable<T> {
    //todo transfer updatedBy to backend
    return this.httpClient.delete<T>(
      `${this.baseRoute}/customercardnumber/delete/${id}/${updatedBy}`
    );
  }

  deleteCustomerSubscription<T>(id: number, updatedBy: number): Observable<T> {
    //todo transfer updatedBy to backend
    return this.httpClient.delete<T>(
      `${this.baseRoute}/customersubscription/delete/${id}/${updatedBy}`
    );
  }

  usaStates() {
    return of(this.states);
  }

  getAllCountries<T>(): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.locationAPIRoot}/getCountries`
    );
  }

  getCustomerCardsOnFile<T>(customerId: number): Observable<T> {
    return this.httpClient.get<T>(
      `${this.baseRoute}/${this.baseTarget}/customercardnumber/get/${customerId}`
    );
  }

  getCustomerLocation<T>(): Observable<T> {
    return this.httpClient.get<T>(
      `${this.baseRoute}/customer/getcustomerlocation`
    );
  }

  saveCustomer<T>(payload: any): Observable<T> {
    if (payload.Id) {
      return this.httpClient.post<T>(
        `${this.baseRoute}/${this.baseTarget}/update`,
        payload
      );
    } else {
      return this.httpClient.post<T>(
        `${this.baseRoute}/${this.baseTarget}/add`,
        payload
      );
    }
  }

  getCustomerTransactions<T>(customerId: number, transactionType: string) {
    return this.httpClient.get<T>(
      `${this.baseRoute}/${this.baseTarget}/customertransactions/${customerId}/${transactionType}`
    );
  }

  saveCustomerContactWithAddress<T>(payload: any): Observable<T> {
    let endpoint =
      payload.Id != 0
        ? 'customercontactperson/update'
        : 'customer/customercontactpersonwithaddress/add';
    return this.httpClient.post<T>(`${this.baseRoute}/` + endpoint, payload);
  }

  saveIndividualCustomerContact<T>(payload: any): Observable<T> {
    let endpoint =
      payload.Id != 0
        ? 'customer/customercontactnumber/update'
        : 'customer/customercontactnumber/add';
    return this.httpClient.post<T>(`${this.baseRoute}/` + endpoint, payload);
  }

  getIndividualCustomerContact<T>(customerId: number): Observable<T> {
    let endpoint =
      'customer/customercontactnumber/GetCustomerContact/' + customerId;
    return this.httpClient.get<T>(`${this.baseRoute}/` + endpoint);
  }

  updateCustomerPhoto<T>(payload: any): Observable<T> {
    return this.httpClient.post<T>(
      `${this.baseRoute}/ecommerce/customer/image/update`,
      payload
    );
  }

  deleteCustomerContactNumber<T>(id: number): Observable<T> {
    return this.httpClient.delete<T>(
      `${this.baseRoute}/customer/customercontactnumber/deletecontact/${id}`
    );
  }

  deleteCustomerAddress<T>(id: number, updatedBy: number): Observable<T> {
    return this.httpClient.post<T>(
      `${this.baseRoute}/customer/deletecontactaddress/${id}/${updatedBy}`,
      {}
    );
  }

  deleteCustomerContactPerson<T>(id: number, updatedBy: number): Observable<T> {
    return this.httpClient.delete<T>(
      `${this.baseRoute}/customer/businesscontactdelete/${id}`
    );
  }
  getCustomerDefaultAddress<T>(customerId: string): Observable<T> {
    // return this.httpClient.get<T>(`${this.baseRoute}/customer/get/${customerId}`);
    return this.httpClient.get<T>(
      `${environment.customerAPIRoot}/customer/defaultaddress/get/${customerId}`
    );
  }

  getCustomerInvoices<T>(customerId: number): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.customerAPIRoot}/customer/customertransactions/${customerId}/INVOICE`
    );
  }

  getCustomerEstimates<T>(customerId: number): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.customerAPIRoot}/customer/customertransactions/${customerId}/ESTIMATE`
    );
  }

  getCustomerRecurringInvoices<T>(customerId: number): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.customerAPIRoot}/customer/customertransactions/${customerId}/RECURRING`
    );
  }

  getAllCustomerSubscriptions<T>(customerId: number): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.customerAPIRoot}/customer/customertransactions/${customerId}/SUBSCRIPTION`
    );
  }

  getCustomerWorkOrders<T>(customerId: number): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.customerAPIRoot}/customer/customertransactions/${customerId}/SUBSCRIPTION`
    );
  }

  getCustomerPaymentForms<T>(customerId: number): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.customerAPIRoot}/customer/customertransactions/${customerId}/PAYMENTFORM`
    );
  }

  getCustomerPaymentLinks<T>(customerId: number): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.customerAPIRoot}/customer/customertransactions/${customerId}/QPL`
    );
  }

  getCustomerBusinessContacts<T>(customerId: number): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.customerAPIRoot}/customer/customercontactpersonfull/get/${customerId}`
    );
  }

  getCustomerByEmailAddress<T>(emailAddress: string): Observable<T> {
    const params = new HttpParams().append('emailaddress', emailAddress);
    return this.httpClient.get<T>(
      `${environment.customerAPIRoot}/customer/emailaddress/get`,
      { params }
    );
  }

  getCustomerACHsById<T>(customerId: number): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.paymentAPIRoot}/api/ach/getcustomerachbycustomerid?id=${customerId}`
    );
  }

  addCustomerACHDetails<T>(payload: AddACH): Observable<T> {
    return this.httpClient.post<T>(
      `${environment.paymentAPIRoot}/api/ach/addpaymentmethod`,
      payload
    );
  }

  updateCustomerACH<T>(payload: ACH): Observable<T> {
    return this.httpClient.post<T>(
      `${environment.paymentAPIRoot}/api/ach/UpdateCustomerACH`,
      payload
    );
  }

  IsCustomerSurchargeable<T>(customerId: number): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.customerAPIRoot}/customer/customercardnumber/get/${customerId}`
    );
  }
}
